import {
  closedLostOpportunityStageName,
  customCloseLostReasonsId,
  ENTITY_TYPES,
  LocationDTO,
  nullCloseLostReasonsId,
} from '@sales-center/common'
import { CustomField } from '@sales-center/common'
import { isValidMuiDate } from '@sales-center/common'
import { customValuesToInputModel } from '@user/components/CustomFields'
import {
  addLeadNote,
  useGetEntityFormDataOnMount,
} from '@user/components/Settings/EntityFormSettings'
import { setEntityAndCustomErrors } from '@user/components/Settings/EntityFormSettings/utility/setEntityAndCustomErrors'
import { ServiceLocation } from '@user/store/entityLocations'
import { ILeadOppProduct } from '@user/store/leadOppProducts'
import {
  IOpportunityPostModel,
  OpportunityLocationInput,
} from '@user/store/opportunity'
import { FormikErrors } from 'formik'
import { createSystemOpportunityName } from '../OpportunityName/functions'
import { IAddOpportunityFormModel } from './typings'
import type { FieldSettingsMap } from '@user/store/entityFormSettings'

export const initialValues: IAddOpportunityFormModel = {
  appData: [],
  assigneeId: '',
  billToExternalLinkId: undefined,
  campaign: null,
  campaignId: null,
  customFields: {
    text: {},
    date: {},
    bool: {},
    multi: {},
    single: {},
    number: {},
  },
  expectedClosingDate: new Date(),
  sourceEntityId: undefined,
  sourceEntityType: undefined,
  isClosedWon: false,
  leadSource: null,
  leadSourceId: null,
  nextActionDate: new Date(),
  note: '',
  noteCode: '',
  opportunityName: '',
  opportunityStageId: '',
  opportunityStageName: '',
  opportunityStageClosedLostIdTemp: '',
  ownerId: '',
  products: '',
  referral: null,
  salesFunnelId: '',
  salesNote: '',
  salesTeamId: '',
  serviceLocations: undefined,
  targetPests: [],
  useSystemName: 'true',
  viewDetail: false,
  preferredMethodOfContact: '',
  preferredTimeOfContact: [],
  closedLostReasonId: -1,
  closedLostCustomReason: '',
}

export function useValidate() {
  const { requiredEntitySettings, customFieldSettings, entitySettingsMap } =
    useGetEntityFormDataOnMount(ENTITY_TYPES.Opportunity)

  let errors: FormikErrors<IAddOpportunityFormModel> = {}

  const execute = (model: IAddOpportunityFormModel) => {
    errors = validate(model, entitySettingsMap)

    errors = setEntityAndCustomErrors(
      requiredEntitySettings,
      model,
      errors,
      customFieldSettings,
    )

    return errors
  }

  return { validate: execute }
}

const validate = (
  model: IAddOpportunityFormModel,
  entitySettingsMap: FieldSettingsMap,
): FormikErrors<IAddOpportunityFormModel> => {
  const errors: FormikErrors<IAddOpportunityFormModel> = {}

  const [requiredAddNote] = entitySettingsMap.get(addLeadNote) ?? [false, true]

  if (!model.opportunityName) {
    errors.opportunityName = 'Required'
  }
  if (model.salesTeamId === '') {
    errors.salesTeamId = 'Required'
  }
  if (model.ownerId === '') {
    errors.ownerId = 'Required'
  }

  if (model.salesFunnelId === '') {
    errors.salesFunnelId = 'Required'
  }
  if (model.expectedClosingDate === null) {
    errors.expectedClosingDate = 'Required'
  }

  if (
    model.expectedClosingDate != null &&
    !isValidMuiDate(model.expectedClosingDate)
  )
    errors.expectedClosingDate = 'Invalid Date'

  if (model.nextActionDate != null && !isValidMuiDate(model.nextActionDate))
    errors.nextActionDate = 'Invalid Date'

  if (!model.opportunityStageId || model.opportunityStageId === '') {
    errors.opportunityStageId = 'Required'
  }

  if (model.products === '') {
    errors.products = 'Required to Save*'
  }

  if (model.opportunityStageName == closedLostOpportunityStageName) {
    if (model.closedLostReasonId === nullCloseLostReasonsId) {
      errors.closedLostReasonId = 'Closed/Lost reason required*'
    } else if (model.closedLostReasonId === customCloseLostReasonsId) {
      if (model.closedLostCustomReason === '') {
        errors.closedLostReasonId = 'Closed/Lost reason required*'
        errors.closedLostCustomReason = 'Custom reason required'
      }
    }
  }

  if (requiredAddNote) {
    if (model.note?.trim() === '') {
      errors.note = 'Required'
    }
  }

  if (model.note?.trim() !== '' && model.addNoteToPestPac) {
    if (!model.noteCode) {
      errors.noteCode = 'Required'
    }
    if (!model.serviceLocation) {
      errors.serviceLocation = 'Required'
    }
  }

  return errors
}

export const FormToPostModel = (
  model: IAddOpportunityFormModel,
  sourceEntityType: ENTITY_TYPES | undefined,
  customFields: CustomField[],
  leadId?: number,
  externalLinkId?: string,
  products?: ILeadOppProduct[],
  serviceLocations?: ServiceLocation[] | LocationDTO[],
  isOppFromOpp?: boolean,
): IOpportunityPostModel => {
  const additionalInformation = {
    campaign: model.campaign,
    campaignId: model.campaignId,
    leadSource: model.leadSource,
    leadSourceId: model.leadSourceId,
    targetPests: `[${model.targetPests.toString()}]`,
    type: 'Manual',
  }

  const salesTeamId = parseInt(model.salesTeamId)
  const locations: OpportunityLocationInput[] = []
  let noteExternalLocationId: string = ''

  if (serviceLocations) {
    serviceLocations.map((loc, index: number) => {
      locations.push({
        billingLocation: false,
        externalLinkId: loc.externalLinkId || loc.id,
        primaryServiceLocation: index === 0,
        salesTeamId,
      })

      if (
        model.addNoteToPestPac &&
        loc.id.toString() === model.serviceLocation
      ) {
        noteExternalLocationId = loc.externalLinkId
      }
    })
  }

  let contactTime = model.preferredTimeOfContact
  if (
    model.preferredTimeOfContact &&
    typeof model.preferredTimeOfContact == 'object'
  ) {
    contactTime = model.preferredTimeOfContact.join(',')
  }

  const opportunity: IOpportunityPostModel = {
    name:
      model.useSystemName === 'true'
        ? createSystemOpportunityName(products!)
        : model.opportunityName,
    salesTeamId: salesTeamId,
    salesFunnelId: parseInt(model.salesFunnelId),
    salesFunnelStageId: parseInt(model.opportunityStageId),
    assigneeId: model.ownerId == 'unassigned' ? '' : model.ownerId,
    expectedClosingDate: model.expectedClosingDate,
    nextActionDate: model.nextActionDate,
    tag: '',
    useSystemName: model.useSystemName === 'true',
    note: model.note,
    ...additionalInformation,
    ...model.referral,
    locations,
    customFieldValues: customValuesToInputModel(model, customFields),
    appData: model.appData,
    salesNote: model.salesNote,
    contactTime: contactTime,
    contactMethod: model.preferredMethodOfContact,
  }

  if (model.closedLostReasonId && model.closedLostReasonId != '-1') {
    opportunity.closedLostReasonId = model.closedLostReasonId
    opportunity.closedLostCustomReason = model.closedLostCustomReason
  }

  if (model.note?.length > 0 && model.addNoteToPestPac) {
    opportunity.noteCode = model.noteCode
    opportunity.noteExternalLocationId = noteExternalLocationId
  }

  if (sourceEntityType === ENTITY_TYPES.Lead) {
    opportunity.leadId = leadId
  } else {
    opportunity.externalLinkId = externalLinkId
    isOppFromOpp
      ? (opportunity.products = JSON.parse(JSON.stringify(products)))
      : (opportunity.products = products)

    if (isOppFromOpp) {
      opportunity.products?.forEach((product) => {
        product.serviceLocationId =
          (serviceLocations as LocationDTO[]).find(
            (x) => x.id === +product.serviceLocationId,
          )?.externalLinkId ?? ''
      })
    }
  }

  return opportunity
}
